import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import * as catalogApi from '../api/endpoints/catalog';
import ProductCard from './ProductCard';
import DecorLeaf from './DecorLeaf';
import { useCart } from '../context/CartContext';
import { useDeliveryLocation } from '../context/DeliveryLocationContext';
import { visibleCategories } from '../utils/categories';
import { isStaleLocationError } from '../utils/staleLocation';
import { SkeletonBlock } from './Skeleton';
import { focusRing, homeRail } from '../styles/ui';

const PICKS_LIMIT = 8;

// "Today's Fresh Picks": a rail of real catalogue products for the browsing
// location — GET /products, the same priced, stocked and offer-decorated
// projection the category pages render, sorted by the backend's best-sellers
// order. The filter pills are the real admin categories from GET /categories.
// Nothing here prices, discounts, ranks or hides a product: every figure and
// badge on a tile is the backend's. Rendered inside the storefront gate, so a
// new location mounts a fresh rail (and fresh requests).
const FreshPicks = () => {
  const { locationVersion } = useDeliveryLocation();
  const { addItem, setQuantity, quantityFor } = useCart();
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState('all');
  const [state, setState] = useState({ status: 'loading', products: [] });
  const rail = useRef(null);

  useEffect(() => {
    let active = true;
    catalogApi
      .listCategories()
      .then((response) => {
        if (active) setCategories(visibleCategories(response.data).filter((c) => !c.isEmpty));
      })
      .catch(() => {
        if (active) setCategories([]);
      });
    return () => {
      active = false;
    };
  }, [locationVersion]);

  useEffect(() => {
    let active = true;
    setState({ status: 'loading', products: [] });
    const params = { limit: PICKS_LIMIT, sort: 'best_sellers' };
    if (filter !== 'all') params.category = filter;
    catalogApi
      .listProducts(params)
      .then((response) => {
        if (!active) return;
        setState({ status: 'ready', products: Array.isArray(response?.data) ? response.data : [] });
      })
      .catch((error) => {
        if (!active || isStaleLocationError(error)) return;
        setState({ status: 'error', products: [] });
      });
    return () => {
      active = false;
    };
  }, [filter, locationVersion]);

  const scrollRail = (direction) => {
    const node = rail.current;
    if (!node) return;
    node.scrollBy({ left: direction * Math.max(240, node.clientWidth * 0.8), behavior: 'smooth' });
  };

  const viewAllTo = filter === 'all' ? '/categories' : `/categories/${filter}`;
  const pill = (active) =>
    `shrink-0 inline-flex items-center h-7 px-3 rounded-full text-[11.5px] font-semibold whitespace-nowrap transition-colors ${focusRing} ${
      active ? 'bg-[#2E8B47] text-white' : 'bg-white border border-[#DCE8DC] text-emerald-950 hover:bg-emerald-50'
    }`;

  if (state.status === 'error') return null;

  return (
    <section id="fresh-picks" aria-labelledby="fresh-picks-heading" className="pt-6 lg:pt-8 pb-[14px] bg-white">
      <div className={homeRail}>
        <div className="flex flex-wrap xl:flex-nowrap items-center gap-x-4 gap-y-3 min-h-[32px]">
          <h2 id="fresh-picks-heading" className="serif text-[22px] font-bold text-emerald-950 leading-none flex items-center gap-2">
            <DecorLeaf className="w-5 text-emerald-600 -rotate-[20deg]" />
            Today&apos;s Fresh Picks
          </h2>

          <div className="order-last xl:order-none w-full xl:w-auto xl:flex-1 min-w-0 flex gap-2 overflow-x-auto no-scrollbar xl:ml-2" role="group" aria-label="Filter picks by category">
            <button type="button" onClick={() => setFilter('all')} aria-pressed={filter === 'all'} className={pill(filter === 'all')}>
              All
            </button>
            {categories.map((category) => (
              <button
                key={category.key}
                type="button"
                onClick={() => setFilter(category.key)}
                aria-pressed={filter === category.key}
                className={pill(filter === category.key)}
              >
                {category.label}
              </button>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-3 shrink-0">
            <Link to={viewAllTo} className={`inline-flex items-center gap-1.5 text-[12px] font-bold text-[#1E7A3C] hover:text-emerald-900 ${focusRing}`}>
              View All Products
              <ArrowRight size={13} strokeWidth={2.5} aria-hidden="true" />
            </Link>
            <span className="hidden sm:flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => scrollRail(-1)}
                aria-label="Scroll picks left"
                className={`w-8 h-8 rounded-full border border-[#DCE8DC] bg-white text-emerald-900 flex items-center justify-center hover:bg-emerald-50 ${focusRing}`}
              >
                <ChevronLeft size={15} strokeWidth={2.5} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => scrollRail(1)}
                aria-label="Scroll picks right"
                className={`w-8 h-8 rounded-full border border-[#DCE8DC] bg-white text-emerald-900 flex items-center justify-center hover:bg-emerald-50 ${focusRing}`}
              >
                <ChevronRight size={15} strokeWidth={2.5} aria-hidden="true" />
              </button>
            </span>
          </div>
        </div>

        {state.status === 'loading' ? (
          <div className="mt-4 flex gap-3.5 overflow-hidden" data-testid="fresh-picks-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonBlock key={i} className="shrink-0 h-[222px] w-[162px] sm:w-[180px] xl:w-[calc((100%-5*14px)/6)] rounded-2xl" />
            ))}
          </div>
        ) : state.products.length === 0 ? (
          <p className="mt-4 text-sm text-ink-secondary">No products to show here right now.</p>
        ) : (
          <ul
            ref={rail}
            data-testid="fresh-picks"
            className="mt-4 flex gap-3.5 overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-px-4 xl:scroll-px-0 -mx-4 px-4 xl:mx-0 xl:px-0 pb-2"
          >
            {state.products.map((product) => (
              <li key={product._id} className="snap-start shrink-0 w-[162px] sm:w-[180px] xl:w-[calc((100%-5*14px)/6)]">
                <ProductCard
                  variant="compact"
                  product={product}
                  quantity={quantityFor(product._id)}
                  onAddToCart={addItem}
                  onSetQuantity={setQuantity}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default FreshPicks;
